import { StrictMode, Suspense, useEffect, useMemo, useState } from "react";
import { RouterProvider } from "react-router";
import { I18nextProvider } from "react-i18next";
import { ThemeProvider } from "@emotion/react";
import NiceModal from "@ebay/nice-modal-react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import i18n from "i18next";
import { theme } from "src/lib/emotion/theme.ts";
import { ClientConfigProvider } from "src/client-config/ClientConfigProvider.tsx";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { ModalIDEnum } from "src/lib/nice-modal-react/nice-modal-react.ts";
import { SkeletonTheme } from "react-loading-skeleton";
import { queryClient } from "src/lib/react-query.tsx";
import { QueryClientProvider } from "@tanstack/react-query";
import { NonConflictingThemeProvider } from "src/lib/emotion/NonConflictingThemeProvider.tsx";
import { AppFallback } from "./AppFallback.tsx";
import { AppVersionChecker } from "./AppVersionChecker.tsx";
import { Snackbar } from "../notifications/Snackbar.tsx";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import "dayjs/locale/de";
import { SnackbarProvider } from "src/notifications/SnackbarProvider.ts";
import { SessionProvider } from "src/App/SessionProvider.tsx";
import { ExonnApiProvider } from "src/exonn-api/ExonnApiProvider.tsx";
import { SystemProvider } from "src/system/SystemProvider.tsx";
import { defaultDataProvider } from "src/data-providers/default.ts";
import { GlobalStyles } from "src/App/GlobalStyles.tsx";
import { UserDataProvider } from "src/user-data/UserDataProvider.tsx";
import { type ClientConfig } from "src/client-config/getClientConfig.ts";
import { LicenseInfo } from "@mui/x-license";
import { createBrowserRouter } from "react-router-dom";
import { buildRoutes } from "src/buildRoutes.tsx";
import type { Router } from "@remix-run/router";
import { useUser } from "src/auth/auth.ts";
import { useAppMeta } from "src/App/useAppMeta.ts";
import { useDebugTranslations } from "src/lib/i18next/useDebugTranslations.ts";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import {
  deDE as datePickerDeDE,
  enUS as datePickerEnUS,
} from "@mui/x-date-pickers/locales";
import { DndProvider } from "react-dnd-multi-backend";
import { MouseToTouchBackend } from "src/lib/react-dnd/MouseToTouchBackend.tsx";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";
import { HiddenCkeditor } from "src/hidden-ckeditor/HiddenCkeditor.tsx";
import { UpdateRequiredModal } from "src/admin/modals/UpdateRequiredModal/UpdateRequiredModal.tsx";
import { SnackbarStore } from "src/notifications/SnackbarStore.ts";
import { mapObjectToArray, unique } from "@exonn/utils";
import { useDebugQueries } from "src/hooks/use-debug-queries.ts";
import { LoginRouteSearchParams } from "src/constants/constants.ts";
import { useWindowMessageEventListeners } from "src/App/useWindowMessageEventListeners.ts";
import { useTrackPointerType } from "src/hooks/use-track-pointer-type.ts";

const resourcesThatHaveTranslations = unique(
  mapObjectToArray(
    import.meta.glob("../admin/translations/*/*.json"),
    (_, path) => {
      const match = path.match(/translations\/([^/]+)\//);
      return match?.[1];
    },
  ),
);

await Promise.all([
  loadTranslations(TranslationNamespaceEnum.Common),

  ...resourcesThatHaveTranslations.map((resource) =>
    loadTranslations(getResourceI18nNamespace(resource)),
  ),
]);

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  NiceModal.show(ModalIDEnum.PreloadError);
});

const searchParams = new URLSearchParams(window.location.search);
const token = searchParams.get(LoginRouteSearchParams.Token);

if (token) {
  localStorage.setItem("authToken", token);
}
/* eslint-disable no-console */
console.log("App build number:", import.meta.env.VITE_BUILD_NUMBER);

export const App = ({ config }: { config: ClientConfig }) => {
  const [lang, setLang] = useState(i18n.language);

  const [router] = useState(() => {
    return createBrowserRouter(
      buildRoutes({
        config,
      }),
    );
  });
  useAppMeta({
    config,
  });

  useEffect(() => {
    i18n.on("languageChanged", (lang) => {
      setLang(lang);
    });
  }, []);

  useDebugTranslations();
  useDebugQueries();
  useTrackPointerType();

  const datePickerLocaleText = (
    {
      en: datePickerEnUS,
      de: datePickerDeDE,
    }[lang] || datePickerDeDE
  ).components.MuiLocalizationProvider.defaultProps.localeText;

  const dataProvider = useMemo(() => defaultDataProvider(config), [config]);
  const [snackbar] = useState(() => new SnackbarStore());
  return (
    <StrictMode>
      <DndProvider options={MouseToTouchBackend}>
        <SystemProvider dataProvider={dataProvider}>
          <QueryClientProvider client={queryClient}>
            <NonConflictingThemeProvider theme={theme}>
              <ThemeProvider theme={theme}>
                <GlobalStyles />
                <SkeletonTheme
                  baseColor={theme.skeleton.baseColor}
                  highlightColor={theme.skeleton.highlightColor}
                >
                  <I18nextProvider i18n={i18n}>
                    <Suspense fallback={<AppFallback />}>
                      <ClientConfigProvider config={config}>
                        <ExonnApiProvider>
                          <UserDataProvider>
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                              adapterLocale={lang}
                              localeText={datePickerLocaleText}
                            >
                              <TooltipProvider>
                                <SnackbarProvider value={snackbar}>
                                  <AppVersionChecker router={router} />
                                  <NiceModal.Provider>
                                    <SessionProvider router={router}>
                                      <InnerApp router={router} />
                                      <HiddenCkeditor />
                                    </SessionProvider>

                                    <UpdateRequiredModal
                                      id={ModalIDEnum.PreloadError}
                                    />

                                    <Snackbar />
                                  </NiceModal.Provider>
                                </SnackbarProvider>
                              </TooltipProvider>
                            </LocalizationProvider>
                          </UserDataProvider>
                        </ExonnApiProvider>
                      </ClientConfigProvider>
                    </Suspense>
                  </I18nextProvider>
                </SkeletonTheme>
              </ThemeProvider>
            </NonConflictingThemeProvider>
            <ReactQueryDevtools
              buttonPosition={"bottom-left"}
              position="bottom"
            />
          </QueryClientProvider>
        </SystemProvider>
      </DndProvider>
    </StrictMode>
  );
};

const InnerApp = ({ router }: { router: Router }) => {
  useUser();
  useWindowMessageEventListeners({ router });

  return <RouterProvider router={router} />;
};
